import { Link } from "react-router-dom";
import { Hero } from "@/components/hero/Hero";
import { useHeroIntro } from "@/components/hero/useHeroIntro";
import { Seo } from "@/components/layout/Seo";
import { PageTransition, Reveal, Stagger } from "@/motion";
import {
  CtaBand,
  ProcessTrack,
  SafeImage,
  Section,
  SectionHead,
  StatsRow,
} from "@/components/ui/Blocks";
import { ButtonLink, LinkArrow } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Eyebrow } from "@/components/ui/Chips";
import { JobCard, PlanCard, ProjectCard, PropertyCard, ServiceCard } from "@/components/cards";
import { SectionLoader } from "@/components/ui/Feedback";
import { EnquiryModal } from "@/components/sections/EnquiryModal";
import { useBlogPosts, useJobs, usePlans, useProjects, useServices, useTurnkeyProjects } from "@/hooks";
import { imagePool, processSteps, stats } from "@/data/site";
import { STATUS_META, STATUS_ORDER } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { useState } from "react";
import type { TurnkeyProject } from "@/types";

const HOME_SERVICES = [
  "architecture",
  "civil-engineering",
  "construction",
  "turnkey-development",
  "project-management",
  "consultancy",
];

const WHY = [
  {
    title: "We tell you the bad news first",
    body: "If the brief does not fit the budget, you hear it in week three — not in month eight when it becomes a crisis.",
  },
  {
    title: "Design and engineering share a room",
    body: "Our structural engineers attend concept reviews. Our architects attend reinforcement inspections. The handover between them does not exist.",
  },
  {
    title: "Specification, not “or equivalent”",
    body: "We procure against named products and document what went in. Substitution is the quiet way projects lose their quality.",
  },
  {
    title: "Numbers that survive scrutiny",
    body: "Total variation on our last major PMC engagement was 3.1% across 34 months. We are happy to be measured on it.",
  },
];

const CREDENTIALS = [
  { icon: "award", label: "IGBC Member" },
  { icon: "shield-check", label: "ISO 9001:2015" },
  { icon: "helmet", label: "Council of Architecture" },
  { icon: "building", label: "RERA Registered" },
  { icon: "users", label: "IIA Affiliated" },
  { icon: "recycle", label: "GRIHA Accredited" },
] as const;

export default function Home() {
  // Stages the hero entrance by toggling classes on <html>; the animation
  // itself is CSS. See components/hero/hero.css.
  useHeroIntro();

  const [enquiry, setEnquiry] = useState<TurnkeyProject | null>(null);

  const projects = useProjects({ sort: "featured" });
  const turnkey = useTurnkeyProjects({ sort: "status" });
  const plans = usePlans({ sort: "popular" });
  const services = useServices();
  const posts = useBlogPosts();
  const jobs = useJobs();

  const featuredProjects = (projects.data ?? []).slice(0, 6);
  const featuredTurnkey = (turnkey.data ?? []).slice(0, 3);
  const featuredPlans = (plans.data ?? []).filter((p) => p.featured).slice(0, 3);
  const homeServices = (services.data ?? []).filter((s) => HOME_SERVICES.includes(s.id));
  const [leadPost, ...restPosts] = posts.data ?? [];

  return (
    <PageTransition>
      <Seo
        title="Indraprastha Engineers & Architects — Dream Spaces. Stronger Futures."
        description="Civil engineering precision meets architectural creativity. Architecture, engineering, construction, turn-key developments and licensed floor plans across North India."
        path="/"
      />

      <Hero />

      {/* ---------------------------------------------------- positioning -- */}
      <Section>
        <div className="intro-grid">
          <Reveal preset="left" className="intro-media">
            <SafeImage
              src={imagePool.villa1}
              alt="Courtyard residence with brick jaali screen and covered verandah"
            />
            <div className="intro-media__badge">
              <span className="value">15</span>
              <span className="label">
                Years of designing, engineering
                <br />
                and building in North India
              </span>
            </div>
          </Reveal>

          <Reveal preset="right">
            <Eyebrow>Who we are</Eyebrow>
            <h2>One practice that draws it, engineers it and builds it.</h2>
            <p className="lead">
              Most projects lose money in the gaps — between the architect and the engineer, between
              the drawing and the site. We removed those gaps by keeping every discipline under one
              roof and one accountable programme.
            </p>

            <div className="intro-points">
              {[
                {
                  icon: "compass",
                  title: "Design that survives its own budget",
                  body: "Costs are tested against the drawing from week one, not discovered at tender.",
                },
                {
                  icon: "helmet",
                  title: "Engineers in the room from concept",
                  body: "Slab depths, grids and service routes are agreed while they are still free to change.",
                },
                {
                  icon: "shield-check",
                  title: "Built to the specification we wrote",
                  body: "Named products, checklist-gated pours, documented results at every stage.",
                },
              ].map((point) => (
                <div key={point.title} className="intro-point">
                  <span className="intro-point__icon">
                    <Icon name={point.icon as "compass"} />
                  </span>
                  <div>
                    <h3>{point.title}</h3>
                    <p>{point.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <ButtonLink to="/about" variant="ghost" icon="arrow-right" className="mt-8">
              More about the practice
            </ButtonLink>
          </Reveal>
        </div>

        <Reveal preset="fade" className="logo-strip mt-12">
          {CREDENTIALS.map((item) => (
            <span key={item.label} className="logo-strip__item">
              <Icon name={item.icon} />
              {item.label}
            </span>
          ))}
        </Reveal>
      </Section>

      {/* -------------------------------------------- three portfolios ---- */}
      <Section tone="tint" aria-labelledby="portfoliosTitle">
        <SectionHead
          id="portfoliosTitle"
          align="center"
          eyebrow="How our work is organised"
          title="Three portfolios, three different purposes"
          lead="We keep client commissions, our own developments and our licensed drawing sets strictly separate — because you are looking for one of them, not all three."
        />

        <Stagger className="grid grid-3 grid-gap-lg">
          {[
            {
              num: "01",
              title: "Completed Projects",
              body: "Work we were commissioned to design, engineer or build for a client. Presented as case studies — the problem, the decisions, the measured result.",
              to: "/projects",
              cta: "Browse case studies",
              points: [
                "Case studies with measured outcomes",
                "Filter by type, city, year and service",
                "Not for sale — these belong to our clients",
              ],
            },
            {
              num: "02",
              title: "Turn-Key Projects",
              body: "Developments we fund, design and build ourselves, offered directly to buyers. Every listing shows its real status, price and possession date.",
              to: "/turnkey",
              cta: "Browse available properties",
              points: [
                "Live status on every development",
                "Configurations, pricing and possession",
                "Enquire, register interest or book a visit",
              ],
              accent: true,
            },
            {
              num: "03",
              title: "Floor Plan Marketplace",
              body: "Ready-to-build drawing sets you can licence today in DWG, DXF, PDF and Revit — every one of them already built at least once.",
              to: "/floor-plans",
              cta: "Browse licensed plans",
              points: [
                "Filter by area, dimensions and flooring",
                "Preview the layout logic for free",
                "Instant download after licensing",
              ],
            },
          ].map((card) => (
            <Reveal key={card.num} className={`split-card${card.accent ? " split-card--accent" : ""}`}>
              <span className="split-card__num">{card.num}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <ul className="split-card__list">
                {card.points.map((point) => (
                  <li key={point}>
                    <Icon name="check-circle" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <LinkArrow to={card.to} className="mt-4">
                {card.cta}
              </LinkArrow>
            </Reveal>
          ))}
        </Stagger>
      </Section>

      {/* ------------------------------------------- completed projects --- */}
      <Section aria-labelledby="featuredTitle">
        <SectionHead
          id="featuredTitle"
          eyebrow="Selected work"
          title="Completed projects"
          lead="Commissioned work delivered for private clients, institutions and developers across eight cities."
          action={
            <ButtonLink to="/projects" variant="ghost" icon="arrow-right">
              All projects
            </ButtonLink>
          }
        />

        {projects.isLoading ? (
          <SectionLoader />
        ) : (
          <Stagger className="project-grid">
            {featuredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </Stagger>
        )}
      </Section>

      {/* ----------------------------------------------------- turn-key --- */}
      <Section tone="tint" aria-labelledby="turnkeyTitle">
        <SectionHead
          id="turnkeyTitle"
          eyebrow="Available now"
          title="Turn-key developments"
          lead="Our own projects, at every stage from masterplan to ready-to-move. Status is shown honestly — including the ones you cannot buy."
          action={
            <ButtonLink to="/turnkey" variant="ghost" icon="arrow-right">
              All properties
            </ButtonLink>
          }
        />

        <Reveal preset="fade" className="turnkey-legend">
          {STATUS_ORDER.map((status) => (
            <span key={status} className="turnkey-legend__item">
              <span
                className="turnkey-legend__dot"
                style={{ background: STATUS_META[status].colour }}
              />
              {STATUS_META[status].label}
            </span>
          ))}
        </Reveal>

        {turnkey.isLoading ? (
          <SectionLoader />
        ) : (
          <Stagger className="property-grid">
            {featuredTurnkey.map((project) => (
              <PropertyCard key={project.id} project={project} onEnquire={setEnquiry} />
            ))}
          </Stagger>
        )}
      </Section>

      {/* -------------------------------------------------- marketplace --- */}
      <Section aria-labelledby="plansTitle">
        <SectionHead
          id="plansTitle"
          eyebrow="Marketplace"
          title="Licence a plan, skip four months of design"
          lead="Ready-to-build drawing sets in DWG, DXF, PDF and Revit. Preview the layout logic free — unlock the full set when you are ready to build."
          action={
            <ButtonLink to="/floor-plans" variant="ghost" icon="arrow-right">
              All plans
            </ButtonLink>
          }
        />

        {plans.isLoading ? (
          <SectionLoader />
        ) : (
          <Stagger className="plan-grid">
            {featuredPlans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </Stagger>
        )}
      </Section>

      {/* ----------------------------------------------------- services --- */}
      <Section tone="tint" aria-labelledby="servicesTitle">
        <SectionHead
          id="servicesTitle"
          eyebrow="What we do"
          title="Services"
          lead="Engage us for one discipline or for the whole chain — the handover between them is where projects usually fail, so we removed it."
          action={
            <ButtonLink to="/services" variant="ghost" icon="arrow-right">
              All services
            </ButtonLink>
          }
        />
        <Stagger className="service-grid">
          {homeServices.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </Stagger>
      </Section>

      {/* ------------------------------------------------------ process --- */}
      <Section tone="surface" aria-labelledby="processTitle">
        <SectionHead
          id="processTitle"
          eyebrow="How we work"
          title="Six stages, one accountable team"
          lead="The same people carry your project from the first site visit to the defect liability period. Nobody hands it over halfway."
        />
        <ProcessTrack steps={processSteps} />
      </Section>

      {/* --------------------------------------------------- why choose --- */}
      <Section aria-labelledby="whyTitle">
        <div className="why-grid">
          <Reveal preset="left">
            <Eyebrow>Why clients stay</Eyebrow>
            <h2 id="whyTitle">Credibility you can check, not just claim</h2>
            <p className="lead">
              Every number below is drawn from delivered work. Ask us for the project it came from
              and we will tell you.
            </p>
            <StatsRow items={stats} className="mt-10" />
            <ButtonLink to="/contact" variant="primary" icon="arrow-right" className="mt-10">
              Discuss your project
            </ButtonLink>
          </Reveal>

          <Stagger className="why-list" stagger={0.08}>
            {WHY.map((item, index) => (
              <Reveal key={item.title} preset="right" delay={index * 0.05} className="why-item">
                <span className="why-item__num">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </Stagger>
        </div>
      </Section>

      {/* ------------------------------------------------------ journal --- */}
      <Section tone="tint" aria-labelledby="journalTitle">
        <SectionHead
          id="journalTitle"
          eyebrow="Journal"
          title="Notes from the studio and the site"
          lead="Technical writing for clients who want to understand the decisions being made on their behalf."
          action={
            <ButtonLink to="/journal" variant="ghost" icon="arrow-right">
              All articles
            </ButtonLink>
          }
        />

        {leadPost ? (
          <div className="journal-preview">
            <Reveal preset="left" className="blog-card journal-feature">
              <Link to={`/journal/${leadPost.id}`} className="blog-card__media" tabIndex={-1} aria-hidden="true">
                <SafeImage src={leadPost.cover} alt={leadPost.title} />
                <span className="blog-card__cat">
                  <span className="badge badge--glass">{leadPost.category}</span>
                </span>
              </Link>
              <div>
                <div className="blog-card__meta mb-4">
                  <span>{formatDate(leadPost.date)}</span>
                  <span className="dot" />
                  <span>{leadPost.readTime} min read</span>
                </div>
                <h3 className="blog-card__title">
                  <Link to={`/journal/${leadPost.id}`}>{leadPost.title}</Link>
                </h3>
                <p className="blog-card__excerpt mt-4">{leadPost.excerpt}</p>
              </div>
            </Reveal>

            <Stagger className="journal-list">
              {restPosts.slice(0, 3).map((post) => (
                <Reveal key={post.id} preset="right" className="journal-item">
                  <Link to={`/journal/${post.id}`} className="journal-item__media" tabIndex={-1} aria-hidden="true">
                    <SafeImage src={post.cover} alt={post.title} />
                  </Link>
                  <div>
                    <span className="mono-label">{post.category}</span>
                    <h3>
                      <Link to={`/journal/${post.id}`}>{post.title}</Link>
                    </h3>
                    <div className="blog-card__meta">
                      <span>{formatDate(post.date)}</span>
                      <span className="dot" />
                      <span>{post.readTime} min read</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </Stagger>
          </div>
        ) : (
          <SectionLoader />
        )}
      </Section>

      {/* ------------------------------------------------------ careers --- */}
      <Section aria-labelledby="careersTitle">
        <div className="careers-preview">
          <Reveal preset="left">
            <Eyebrow>Careers</Eyebrow>
            <h2 id="careersTitle">Build things that stay built</h2>
            <p className="lead">
              A hundred and ten people across three studios. Architects who visit site, engineers who
              sit in design reviews, and a progression path attached to named responsibility rather
              than years served.
            </p>
            <ButtonLink to="/careers" variant="primary" icon="arrow-right" className="mt-8">
              See open positions
            </ButtonLink>
          </Reveal>

          <Stagger className="careers-preview__list">
            {(jobs.data ?? []).slice(0, 3).map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </Stagger>
        </div>
      </Section>

      <CtaBand
        eyebrow="Let's begin"
        title="Have a project in mind?"
        body="Tell us the site, the brief and the budget. We will tell you honestly whether the three fit together — and what we would change if they do not."
        primary={{ label: "Discuss Your Project", to: "/contact" }}
        secondary={{ label: "+91 124 456 7890", to: "tel:+911244567890", external: true }}
      />

      <EnquiryModal project={enquiry} onClose={() => setEnquiry(null)} />
    </PageTransition>
  );
}

